var NTS_CART_BUILD = "20260603-cachebust-1";
/**
 * nts-hl-cart.js — v3
 * CartFacade — single public API for all cart operations via GQL Storefront API.
 *
 * Owns: semaphore queue, GraphQL mutations, background verify.
 * Emits: cart:api:start, cart:api:success, cart:api:error, cart:api:idle,
 *        cart:api:queued, cart:batch:resolved, cart:batch:empty,
 *        cart:synced, cart:mismatch, cart:server:full, cart:verify:error
 */

/* ── GQL Fragments ── */
const CART_FRAGMENT = `
  fragment CartFields on Cart {
    id checkoutUrl totalQuantity
    cost {
      subtotalAmount { amount currencyCode }
      totalAmount    { amount currencyCode }
    }
    discountCodes { code applicable }
    lines(first: 20) {
      nodes {
        id quantity
        attributes { key value }
        cost { totalAmount { amount currencyCode } }
        merchandise {
          ... on ProductVariant {
            id title
            price            { amount currencyCode }
            compareAtPrice   { amount currencyCode }
            image { url }
            product { id title handle productType featuredImage { url } }
          }
        }
      }
    }
  }
`;

const M_CART_CREATE = `${CART_FRAGMENT}
  mutation cartCreate($lines:[CartLineInput!]!, $discountCodes:[String!], $attributes:[AttributeInput!]) {
    cartCreate(input:{ lines:$lines, discountCodes:$discountCodes, attributes:$attributes }) {
      cart { ...CartFields } userErrors { field message }
    }
  }`;

const M_CART_LINES_ADD = `${CART_FRAGMENT}
  mutation cartLinesAdd($cartId:ID!, $lines:[CartLineInput!]!) {
    cartLinesAdd(cartId:$cartId, lines:$lines) {
      cart { ...CartFields } userErrors { field message }
    }
  }`;

const M_CART_LINES_UPDATE = `${CART_FRAGMENT}
  mutation cartLinesUpdate($cartId:ID!, $lines:[CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId:$cartId, lines:$lines) {
      cart { ...CartFields } userErrors { field message }
    }
  }`;

const M_CART_LINES_REMOVE = `${CART_FRAGMENT}
  mutation cartLinesRemove($cartId:ID!, $lineIds:[ID!]!) {
    cartLinesRemove(cartId:$cartId, lineIds:$lineIds) {
      cart { ...CartFields } userErrors { field message }
    }
  }`;

const Q_CART = `${CART_FRAGMENT}
  query cartQuery($cartId:ID!) { cart(id:$cartId) { ...CartFields } }`;

const M_CART_DISCOUNT = `${CART_FRAGMENT}
  mutation cartDiscountCodesUpdate($cartId:ID!, $discountCodes:[String!]) {
    cartDiscountCodesUpdate(cartId:$cartId, discountCodes:$discountCodes) {
      cart { ...CartFields } userErrors { field message }
    }
  }`;

// Add lines AND set the discount in ONE request. GraphQL runs top-level mutation
// fields in series, so cartLinesAdd (product + SI + free gift) lands first, then
// cartDiscountCodesUpdate applies the auto-coupon — all in a single round-trip,
// so the gift resolves to $0 together with the add instead of after a 2nd call.
const M_CART_LINES_ADD_WITH_DISCOUNT = `${CART_FRAGMENT}
  mutation addAndDiscount($cartId:ID!, $lines:[CartLineInput!]!, $discountCodes:[String!]) {
    cartLinesAdd(cartId:$cartId, lines:$lines) {
      cart { ...CartFields } userErrors { field message }
    }
    cartDiscountCodesUpdate(cartId:$cartId, discountCodes:$discountCodes) {
      cart { ...CartFields } userErrors { field message }
    }
  }`;


class CartFacade {
  constructor({ bus, cache, rules, config }) {
    this._bus    = bus;
    this._cache  = cache;
    this._rules  = rules;
    this._config = config;
    this._queue  = [];
    this._busy   = false;
    this._endpoint = config.store.domain + '/api/2024-01/graphql.json';
    // Variants the shopper has hit delete on but whose server removal hasn't
    // finished. The drawer hides these immediately (instant delete), and they
    // stay hidden so an add still in flight can't visually resurrect the line.
    this._pendingRemove = new Set();
  }

  /** True while a delete is optimistically in progress for this variant. */
  isPendingRemove(variantId) { return this._pendingRemove.has(Number(variantId)); }

  /* ── PUBLIC API ── */

  async add(items, trigger = 'single', opts = {}) {
    return this._enqueue(() => this._add(items, trigger, opts), { action: 'add', trigger });
  }

  async addBundle() {
    const items = this._config.bundle.map(b => ({
      merchandiseId: 'gid://shopify/ProductVariant/' + b.variantId,
      quantity: b.quantity || 1,
    }));
    return this.add(items, 'bundle');
  }

  async remove(lineId) {
    // Capture the variant at click time. If the user deletes fast — before a
    // still-in-flight add has reconciled this optimistic line into a real one —
    // the queued op would otherwise carry a dead lineId and remove nothing.
    // Passing the variant lets _remove re-target the real line at run time.
    const snap = (this._cache.getCart().items || []).find(i => i.lineId === lineId);
    const variantId = snap ? Number(snap.variantId) : null;
    const pended = [];
    const pend = (vid) => {
      vid = Number(vid);
      if (!this._pendingRemove.has(vid)) { this._pendingRemove.add(vid); pended.push(vid); }
    };
    if (variantId != null) {
      // INSTANT delete: mark the variant pending and re-render now so the line
      // vanishes on click — no waiting for the in-flight add / late price to
      // settle. The pending flag also makes the drawer keep it hidden, so the
      // add's server response can't flash it back before the real remove lands.
      pend(variantId);
      // If this delete clears the last real product, hide the auto-added free
      // gift(s) and Ship-Insure in the SAME render — so the drawer drops
      // straight to the empty state instantly instead of leaving the gift /
      // insurance lingering until the server round-trip strips them.
      const items = this._cache.getCart().items || [];
      const realLeft = items.some(i =>
        Number(i.variantId) !== variantId &&
        !this._pendingRemove.has(Number(i.variantId)) &&
        !this._isAddonItem(i)
      );
      if (!realLeft) items.forEach(i => { if (this._isAddonItem(i)) pend(i.variantId); });
      this._bus.emit('cart:updated', { cart: this._cache.getCart(), source: 'pending-remove' });
    }
    const p = this._enqueue(() => this._remove(lineId, variantId), { action: 'remove', lineId });
    // Clear the guard(s) once the op settles. On success the lines are already
    // gone from the cache; on failure the error handler re-syncs, so dropping
    // the guard lets any still-present line reappear instead of staying hidden.
    if (pended.length) p.then(() => this._unpendMany(pended), () => this._unpendMany(pended));
    return p;
  }

  /** SI line, or a configured/flagged free-gift line — cart "chrome", not a product. */
  _isAddonItem(i) {
    const siId = this._config.shipInsure && this._config.shipInsure.variantId;
    if (siId != null && Number(i.variantId) === Number(siId)) return true;
    if ((i.attributes || []).some(a => a.key === '_gift' || a.key === '_si')) return true;
    const bundles = this._config.bundles || {};
    for (const k in bundles) {
      const g = bundles[k].freeGift;
      if (g && Number(g.variantId) === Number(i.variantId)) return true;
    }
    return false;
  }

  _unpendMany(variantIds) {
    let changed = false;
    (variantIds || []).forEach(v => { if (this._pendingRemove.delete(Number(v))) changed = true; });
    if (changed) this._bus.emit('cart:updated', { cart: this._cache.getCart(), source: 'pending-clear' });
  }

  async removeByVariant(variantId) {
    const cart = this._cache.getCart();
    const item = cart.items.find(i => i.variantId === variantId);
    if (!item?.lineId) return { ok: false, reason: 'not found' };
    return this.remove(item.lineId);
  }

  async updateQty(lineId, quantity) {
    if (quantity <= 0) return this.remove(lineId);
    // Same fast-interaction guard as remove(): capture the variant so a qty
    // change that fires before an in-flight add settles still hits the real line.
    const snap = (this._cache.getCart().items || []).find(i => i.lineId === lineId);
    return this._enqueue(() => this._updateQty(lineId, quantity, snap ? snap.variantId : null), { action: 'update', lineId });
  }

  async applyDiscount(code) {
    return this._enqueue(() => this._applyDiscount(code), { action: 'discount' });
  }

  /**
   * Remove a coupon from the pill AND opt out of it for the rest of the session:
   * the auto-matcher will skip it, so it won't re-apply on the next add. Also
   * strips the code's free gift and drops the code from the cart + cart-create
   * defaults. (To re-enable, reload with the ?token= URL.)
   */
  async optOutCoupon(code) {
    const up = String(code).toUpperCase();
    if (this._config.optedOutCodes) this._config.optedOutCodes.add(up);
    if (Array.isArray(this._config.discountCodes)) {
      this._config.discountCodes = this._config.discountCodes.filter(c => String(c).toUpperCase() !== up);
    }
    // Pull the gift(s) tied to this code (queued; _reconcileOrphanGift after the
    // discount update is a backstop in case any slip through).
    const bundles = this._config.bundles || {};
    Object.keys(bundles).forEach((k) => {
      const sc = bundles[k];
      if (sc.discountCode && sc.freeGift && sc.discountCode.toUpperCase() === up) {
        this.removeByVariant(sc.freeGift.variantId);
      }
    });
    const remaining = (this._cache.getCart().discountCodes || [])
      .map(d => d.code).filter(c => String(c).toUpperCase() !== up);
    return this.applyDiscount(remaining);
  }

  async clear() {
    return this._enqueue(() => this._clear(), { action: 'clear' });
  }

  getCart() { return this._cache.getCart(); }

  /**
   * Resolve once the background queue is fully drained (no in-flight or pending
   * server ops). The UI runs optimistically and never blocks on this — it's
   * only awaited at checkout, so we hand off a server cart that matches the UI.
   */
  whenIdle() {
    if (!this._busy && this._queue.length === 0) return Promise.resolve();
    return new Promise((resolve) => {
      const off = this._bus.on('cart:api:idle', () => {
        if (!this._busy && this._queue.length === 0) { off(); resolve(); }
      });
    });
  }

  async sync() {
    return this._enqueue(() => this._sync(), { action: 'sync' });
  }

  /* ── SEMAPHORE QUEUE ── */

  _enqueue(operation, meta = {}) {
    return new Promise((resolve, reject) => {
      this._queue.push({ operation, resolve, reject, meta });
      if (this._queue.length > 1) {
        this._bus.emit('cart:api:queued', { position: this._queue.length, ...meta });
      }
      this._drain();
    });
  }

  async _drain() {
    if (this._busy || this._queue.length === 0) return;
    this._busy = true;
    const { operation, resolve, reject, meta } = this._queue.shift();
    this._bus.emit('cart:api:start', meta);
    try {
      const result = await operation();
      resolve(result);
    } catch (e) {
      this._bus.emit('cart:api:error', { error: e.message, ...meta });
      reject(e);
    } finally {
      this._busy = false;
      this._bus.emit('cart:api:idle', meta);
      this._drain();
    }
  }

  /* ── OPERATIONS ── */

  async _add(items, trigger, opts = {}) {
    // 0. Re-add fast-path — if a single product ATC targets a variant already in
    // the cart as a real (non-gift, non-SI) line, increment that line instead of
    // issuing another add. cartLinesAdd would briefly surface a second line
    // before merging server-side (and the optimistic layer a duplicate row);
    // a quantity update touches the one existing line, so nothing flashes. The
    // free gift / discount were already resolved on the first add, so re-running
    // the scenario matcher here isn't needed.
    if (trigger === 'single' && items.length === 1) {
      const vId = Number(String(items[0].merchandiseId).split('/').pop());
      const siId = this._config.shipInsure && this._config.shipInsure.variantId;
      const isAddon = (i) => i.variantId === siId ||
        (i.attributes || []).some(a => a.key === '_gift' || a.key === '_si');
      const cart = this._cache.getCart();
      const line = (cart.items || []).find(i =>
        i.variantId === vId &&
        String(i.lineId).indexOf('optimistic-') !== 0 &&
        !isAddon(i)
      );
      if (line) {
        const cap = this._config.rules && this._config.rules.quantityCap;
        const max = (cap && cap.overrides && cap.overrides[vId]) || (cap && cap.defaultMax) || 99;
        const newQty = Math.min((line.quantity || 0) + (items[0].quantity || 1), max);
        if (newQty <= (line.quantity || 0)) {
          this._bus.emit('cart:batch:empty', { skipped: [{ id: vId, reason: 'at max ' + max }], trigger });
          return { ok: true, cart: this._cache.getCart(), capped: true };
        }
        return this._updateQty(line.lineId, newQty);
      }
    }

    // 1. Rules pipeline
    const ctx = this._rules.resolve(items, trigger, this._cache.getCart());

    if (ctx.batch.length === 0) {
      this._bus.emit('cart:batch:empty', { skipped: ctx.skipped, trigger });
      return { ok: false, reason: 'empty', skipped: ctx.skipped };
    }

    // 1.4 Scenario detection — if any item in the batch is a qualifying product
    // for one of the BUNDLES scenarios, inject the scenario's free gift and
    // queue its discount code to apply. First match wins (BUNDLES enumeration
    // order in nts-hl-config.js — re-order there to change priority).
    // Build a variantId -> {productType, handle, title} lookup from the
    // optimistic items the caller supplied (product-repo passes full metadata),
    // so the scenario matcher can run the shared eligibility predicate at ATC
    // time — the batch lines themselves only carry merchandiseId.
    const metaMap = new Map();
    (opts.optimisticItems || []).forEach((o) => {
      metaMap.set(Number(o.variantId), {
        variantId: Number(o.variantId),
        productType: o.productType || '',
        handle: o.handle || '',
        title: o.title || '',
      });
    });
    // ONE coupon at a time: take only the highest-priority match. Order is
    // coupons.json order — list specific (requireHandles) coupons BEFORE the
    // catch-all travel coupon so a targeted product gets its own coupon, not
    // both. (_matchScenarios returns all matches in order; we use the first.)
    const matchedScenarios = this._matchScenarios(ctx.batch, metaMap).slice(0, 1);
    const giftCfgByVariant = new Map();   // variantId -> freeGift config (optimistic render)
    const scenarioCodes = [];
    matchedScenarios.forEach((sc) => {
      if (sc.discountCode && scenarioCodes.indexOf(sc.discountCode) === -1) {
        scenarioCodes.push(sc.discountCode);
      }
      if (!sc.freeGift) return;
      const giftId = sc.freeGift.variantId;
      giftCfgByVariant.set(giftId, sc.freeGift);
      const inCart = (this._cache.getCart().items || []).some(i => i.variantId === giftId);
      const inBatch = ctx.batch.some(b =>
        Number(String(b.merchandiseId).split('/').pop()) === giftId
      );
      if (!inCart && !inBatch) {
        ctx.batch.push({
          merchandiseId: 'gid://shopify/ProductVariant/' + giftId,
          quantity: 1,
          attributes: [{ key: '_gift', value: sc.key }],
        });
      }
    });

    this._bus.emit('cart:batch:resolved', {
      batch: ctx.batch, skipped: ctx.skipped, injected: ctx.injected,
      scenarios: matchedScenarios.map(s => s.key), trigger,
    });

    // 1.5 Optimistic write — render items in the drawer immediately while the
    // GQL call is in flight. We map ctx.batch (final lines incl. rule-injected
    // SI and scenario-injected free gifts) onto display data: caller-provided
    // opts.optimisticItems take priority by variantId, with SI/gift metadata
    // pulled from config. The cache replaces these with server truth on
    // cart:api:success.
    if (opts.optimisticItems?.length) {
      const userByVariant = new Map(
        opts.optimisticItems.map(o => [Number(o.variantId), o])
      );
      const siCfg = this._config.rules?.shipInsure || this._config.shipInsure;
      const optimisticBatch = ctx.batch.map(b => {
        const variantId = Number(String(b.merchandiseId).split('/').pop());
        const user = userByVariant.get(variantId);
        if (user) return { ...user, variantId, quantity: b.quantity || user.quantity || 1, attributes: b.attributes || [] };
        if (siCfg && siCfg.variantId === variantId) {
          return {
            variantId,
            title: 'Ship-Insure',
            variantTitle: null,
            handle: 'ship-insure',
            price: siCfg.price || 0,
            image: null,
            quantity: b.quantity || 1,
            attributes: b.attributes || [],
          };
        }
        const giftCfg = giftCfgByVariant.get(variantId);
        if (giftCfg) {
          return {
            variantId,
            title: giftCfg.title || 'Free gift',
            variantTitle: giftCfg.variantTitle || null,
            handle: '',
            price: 0,                          // optimistic: render FREE immediately
            comparePrice: giftCfg.price || null, // strike-through reference
            image: giftCfg.image || null,
            quantity: 1,
            attributes: b.attributes || [{ key: '_gift', value: 'gift' }],
          };
        }
        return null;
      }).filter(Boolean);
      if (optimisticBatch.length) this._cache.optimisticAdd(optimisticBatch);
    }

    // 1.6 Optimistic discount codes — show the pill(s) immediately alongside the
    // optimistic gift(s), instead of popping in after the server round-trip.
    if (scenarioCodes.length) {
      const codesNow = (this._cache.getCart().discountCodes || []).map(d => d.code);
      const merged = codesNow.slice();
      scenarioCodes.forEach((c) => { if (merged.indexOf(c) === -1) merged.push(c); });
      if (merged.length !== codesNow.length) this._cache.optimisticDiscounts(merged);
    }

    // 2. Choose: cartCreate (new) or cartLinesAdd (existing)
    const cached = this._cache.getCart();
    let gqlCart;

    if (cached.shopifyCartId) {
      // Merge the codes we want on the cart (existing + matched scenario codes).
      const existingCodes = (this._cache.getCart().discountCodes || []).map(d => d.code);
      const wantCodes = existingCodes.slice();
      scenarioCodes.forEach((c) => { if (wantCodes.indexOf(c) === -1) wantCodes.push(c); });
      const needsDiscount = scenarioCodes.length && wantCodes.length !== existingCodes.length;

      if (needsDiscount) {
        // Add lines + apply coupon in ONE round-trip so product/SI/gift and the
        // discount all land together (gift prices to $0 with the add).
        const data = await this._gql(M_CART_LINES_ADD_WITH_DISCOUNT, {
          cartId: cached.shopifyCartId, lines: ctx.batch, discountCodes: wantCodes,
        });
        const addRes = data.cartLinesAdd;
        if (addRes.userErrors?.length) throw new Error(addRes.userErrors.map(e => e.message).join(', '));
        const dcRes = data.cartDiscountCodesUpdate;
        if (dcRes.userErrors?.length) {
          // Non-fatal: items added but discount didn't apply — fall back to the
          // post-add cart so the lines still render.
          this._bus.emit('cart:api:error', {
            error: 'discount: ' + dcRes.userErrors.map(e => e.message).join(', '),
          });
        }
        gqlCart = (dcRes && !dcRes.userErrors?.length && dcRes.cart) ? dcRes.cart : addRes.cart;
      } else {
        // Plain add (no new auto-coupon to apply).
        const data = await this._gql(M_CART_LINES_ADD, {
          cartId: cached.shopifyCartId,
          lines: ctx.batch,
        });
        const result = data.cartLinesAdd;
        if (result.userErrors?.length) throw new Error(result.userErrors.map(e => e.message).join(', '));
        gqlCart = result.cart;
      }
    } else {
      // Create new cart — include discount codes + cart attributes. Merge
      // config-level codes (URL token / explicit) with the matched scenarios.
      const codes = (this._config.discountCodes || []).slice();
      scenarioCodes.forEach((c) => { if (codes.indexOf(c) === -1) codes.push(c); });

      const vars = { lines: ctx.batch };
      if (codes.length) vars.discountCodes = codes;
      if (this._config.cartAttributes?.length) vars.attributes = this._config.cartAttributes;
      const data = await this._gql(M_CART_CREATE, vars);
      const result = data.cartCreate;
      if (result.userErrors?.length) throw new Error(result.userErrors.map(e => e.message).join(', '));
      gqlCart = result.cart;
    }

    this._bus.emit('cart:api:success', {
      cart: gqlCart, action: 'add', trigger, injected: ctx.injected,
      scenarios: matchedScenarios.map(s => s.key),
    });

    return {
      ok: true,
      cart: this._cache.getCart(),
      injected: ctx.injected,
      skipped: ctx.skipped,
    };
  }

  /**
   * Scan a batch (CartLineInput[]) for any item whose variantId matches one of
   * the scenarios' qualifying products. Returns the FIRST matching scenario
   * (BUNDLES enumeration order in nts-hl-config.js controls priority — e.g.
   * `orderNt26` matches Day/Night Set before `moreSavings`, `clear26` matches
   * Detox Oil before `shipAnywhere`).
   */
  _matchScenarios(batch, metaMap) {
    // Token gate: a coupon auto-applies ONLY when its code matches the active
    // URL token (config.tokenCode, e.g. BE374 → TRAVEL). No token → no auto
    // coupon at all. This makes the travel coupon apply to any qualifying
    // product when the funnel token is present, and ignores per-product coupons.
    const tokenCode = this._config.tokenCode;
    if (!tokenCode) return [];
    const tokenCodeUpper = String(tokenCode).toUpperCase();
    // Opt-out: if the shopper removed this coupon from the pill, never auto-apply
    // it again — even though the token is active.
    const optedOut = this._config.optedOutCodes;
    if (optedOut && optedOut.has(tokenCodeUpper)) return [];

    const bundles = this._config.bundles || {};
    const meta = metaMap || new Map();
    const batchVariantIds = batch.map(b =>
      Number(String(b.merchandiseId).split('/').pop())
    );
    const matches = [];
    for (const key in bundles) {
      const scenario = bundles[key];
      // Only the token's coupon auto-fires; skip every other coupon.
      if (!scenario.discountCode || scenario.discountCode.toUpperCase() !== tokenCodeUpper) continue;
      // matchAll scenarios fire when at least one batch item passes the
      // scenario's `qualify` predicate (the SHARED Layer-1 eligibility check
      // that mirrors the server Function). ALL matching scenarios are returned
      // so one product can trigger more than one coupon's gift.
      if (scenario.matchAll) {
        if (typeof scenario.qualify === 'function') {
          const qualifies = batchVariantIds.some((id) => scenario.qualify(meta.get(id) || { variantId: id }));
          if (qualifies) matches.push({ key, ...scenario });
          continue;
        }
        const exclude = (scenario.excludeVariantIds || []).map(Number);
        if (batchVariantIds.some(id => exclude.indexOf(id) === -1)) {
          matches.push({ key, ...scenario });
        }
        continue;
      }
      const qualifying = (scenario.items || []).map(i => i.variantId);
      if (qualifying.length && batchVariantIds.some(id => qualifying.indexOf(id) >= 0)) {
        matches.push({ key, ...scenario });
      }
    }
    return matches;
  }

  /**
   * After a removal/qty change, drop an injected free gift that is no longer
   * justified — i.e. the gift line is present but NO remaining non-gift item
   * still passes the scenario's qualify predicate. Prevents the "free gift
   * lingers and gets charged" trap (the server Function would price it at full
   * since the cart no longer qualifies). Enqueues a normal remove; the
   * resulting cart:server:full re-runs this and no-ops once the gift is gone.
   */
  _reconcileOrphanGift() {
    const cart = this._cache.getCart();
    const codesOnCart = (cart.discountCodes || []).map(d => String(d.code).toUpperCase());
    const bundles = this._config.bundles || {};
    for (const key in bundles) {
      const sc = bundles[key];
      if (!sc.freeGift) continue;
      const giftId = sc.freeGift.variantId;
      const giftLine = (cart.items || []).find(i => i.variantId === giftId);
      if (!giftLine || !giftLine.lineId) continue;
      if (String(giftLine.lineId).indexOf('optimistic-') === 0) continue;
      const stillQualifies = (cart.items || []).some(i =>
        i.variantId !== giftId &&
        (typeof sc.qualify === 'function' ? sc.qualify(i) : true)
      );
      // The gift is only justified while its coupon is on the cart AND a
      // qualifying product remains. If either is false, drop the gift so it
      // never lingers at full price — but DON'T touch the code: the coupon
      // persists across product removals until the shopper removes the pill.
      const codePresent = !sc.discountCode || codesOnCart.indexOf(sc.discountCode.toUpperCase()) >= 0;
      if (!stillQualifies || !codePresent) {
        this.remove(giftLine.lineId);  // queued; safe — re-check no-ops next pass
        return;
      }
    }
  }

  /**
   * Drop Ship-Insure once nothing but add-ons is left (no real, non-gift
   * product remains) — insurance over an empty cart makes no sense. _remove
   * handles this inline for the common case, but if SI's line was still
   * `optimistic-` at removal time the inline strip skips it; this catches that
   * race after the server confirms, and self-heals carts already left in that
   * state. Idempotent: re-runs no-op once SI is gone.
   */
  _reconcileOrphanSI() {
    const siId = this._config.shipInsure && this._config.shipInsure.variantId;
    if (!siId) return;
    const cart = this._cache.getCart();
    const siLine = (cart.items || []).find(i => i.variantId === siId);
    if (!siLine || !siLine.lineId || String(siLine.lineId).indexOf('optimistic-') === 0) return;
    const bundles = this._config.bundles || {};
    const giftIds = Object.keys(bundles)
      .map(k => bundles[k].freeGift && bundles[k].freeGift.variantId)
      .filter(Boolean);
    const realProductLeft = (cart.items || []).some(i =>
      i.variantId !== siId && giftIds.indexOf(i.variantId) === -1
    );
    if (!realProductLeft) this.remove(siLine.lineId);  // queued; safe
  }

  async _remove(lineId, variantId = null) {
    // Re-resolve at run time: if the captured lineId is gone (an in-flight add
    // reconciled an optimistic line into a real one between click and run),
    // fall back to the variant and remove whatever line now holds it — so a
    // fast delete still lands instead of silently no-op'ing.
    const live = this._cache.getCart();
    if (variantId != null && !(live.items || []).some(i => i.lineId === lineId)) {
      const byVariant = (live.items || []).find(i => i.variantId === variantId);
      if (byVariant && byVariant.lineId) lineId = byVariant.lineId;
    }
    // Optimistic ghost — no server line exists; prune locally instead of
    // sending a fake lineId (server would return cart:null → spinner hang).
    if (String(lineId).indexOf('optimistic-') === 0) {
      this._cache.pruneOptimistic(true);
      return { ok: true, cart: this._cache.getCart() };
    }
    const cached = this._cache.getCart();
    if (!cached.shopifyCartId) return { ok: false, reason: 'no cart' };

    // Compute the reconcile UP FRONT (before any network): if removing this
    // line orphans a free gift, gather the gift line + the code to drop so we
    // can do it all in ONE instant UI update + batched server calls — instead
    // of waiting for the remove round-trip, then chaining more round-trips.
    const remaining = (cached.items || []).filter(i => i.lineId !== lineId);
    const removeIds = [lineId];
    let codesAfter = null;
    const bundles = this._config.bundles || {};
    const siId = this._config.shipInsure && this._config.shipInsure.variantId;
    const giftIds = Object.keys(bundles)
      .map(k => bundles[k].freeGift && bundles[k].freeGift.variantId)
      .filter(Boolean);

    for (const key in bundles) {
      const sc = bundles[key];
      if (!sc.freeGift) continue;
      const giftLine = remaining.find(i => i.variantId === sc.freeGift.variantId);
      if (!giftLine || !giftLine.lineId || String(giftLine.lineId).indexOf('optimistic-') === 0) continue;
      const stillQualifies = remaining.some(i =>
        i.variantId !== sc.freeGift.variantId &&
        (typeof sc.qualify === 'function' ? sc.qualify(i) : true)
      );
      if (!stillQualifies) {
        // Remove the orphaned gift, but KEEP the coupon code on the cart — the
        // shopper may add another qualifying product next, and the coupon should
        // persist until they remove it from the pill themselves.
        removeIds.push(giftLine.lineId);
      }
    }

    // Ship-Insure: once no real product (non-gift, non-SI) remains, drop SI too
    // — in the SAME batched removal so it disappears instantly with everything
    // else instead of lingering as insurance over an empty cart.
    const realProductLeft = remaining.some(i =>
      i.variantId !== siId && giftIds.indexOf(i.variantId) === -1
    );
    if (!realProductLeft && siId) {
      const siLine = remaining.find(i => i.variantId === siId);
      if (siLine && siLine.lineId && String(siLine.lineId).indexOf('optimistic-') !== 0
          && removeIds.indexOf(siLine.lineId) === -1) {
        removeIds.push(siLine.lineId);
      }
    }

    // 1) Instant UI: drop the line(s) + clear the code right away.
    this._cache.optimisticRemove(removeIds);
    if (codesAfter) this._cache.optimisticDiscounts(codesAfter);

    // 2) Server (background relative to the UI which already updated): remove
    //    all lines in one mutation, then update codes if needed.
    const data = await this._gql(M_CART_LINES_REMOVE, {
      cartId: cached.shopifyCartId, lineIds: removeIds,
    });
    const result = data.cartLinesRemove;
    if (result.userErrors?.length) throw new Error(result.userErrors.map(e => e.message).join(', '));
    if (!result.cart) throw new Error('Cart not found — it may have expired');
    let finalCart = result.cart;
    if (codesAfter) {
      const dc = await this._gql(M_CART_DISCOUNT, {
        cartId: cached.shopifyCartId, discountCodes: codesAfter,
      });
      if (!dc.cartDiscountCodesUpdate.userErrors?.length) finalCart = dc.cartDiscountCodesUpdate.cart;
    }
    this._bus.emit('cart:server:full', { cart: finalCart, source: 'remove' });
    // Server-confirmed cart: re-check for an orphaned gift/SI whose line was
    // still optimistic when the inline strip above ran (now has a real lineId).
    this._reconcileOrphanGift();
    this._reconcileOrphanSI();
    return { ok: true, cart: this._cache.getCart() };
  }

  async _updateQty(lineId, quantity, variantId = null) {
    // Same run-time re-resolution as _remove: target the real line if the
    // optimistic id was replaced by an add that settled after the click.
    const live = this._cache.getCart();
    if (variantId != null && !(live.items || []).some(i => i.lineId === lineId)) {
      const byVariant = (live.items || []).find(i => i.variantId === variantId);
      if (byVariant && byVariant.lineId) lineId = byVariant.lineId;
    }
    if (String(lineId).indexOf('optimistic-') === 0) {
      this._cache.pruneOptimistic(true);
      return { ok: true, cart: this._cache.getCart() };
    }
    const cached = this._cache.getCart();
    if (!cached.shopifyCartId) return { ok: false, reason: 'no cart' };
    // Instant UI: reflect the new quantity now, server confirms after.
    this._cache.optimisticQty(lineId, quantity);
    const data = await this._gql(M_CART_LINES_UPDATE, {
      cartId: cached.shopifyCartId, lines: [{ id: lineId, quantity }],
    });
    const result = data.cartLinesUpdate;
    if (result.userErrors?.length) throw new Error(result.userErrors.map(e => e.message).join(', '));
    if (!result.cart) throw new Error('Cart not found — it may have expired');
    this._bus.emit('cart:server:full', { cart: result.cart, source: 'update' });
    this._reconcileOrphanGift();
    return { ok: true, cart: this._cache.getCart() };
  }

  async _applyDiscount(input) {
    const cached = this._cache.getCart();
    if (!cached.shopifyCartId) return { ok: false, reason: 'no cart' };
    // cartDiscountCodesUpdate REPLACES the cart's codes array, so the caller
    // passes the intended end state. Accepts a single string (legacy) or an
    // array (drawer coupon UI passes existing + new on add, existing - X on remove).
    const codes = Array.isArray(input)
      ? input.map(c => String(c).trim()).filter(Boolean)
      : (input ? [String(input).trim()] : []);
    const data = await this._gql(M_CART_DISCOUNT, {
      cartId: cached.shopifyCartId, discountCodes: codes,
    });
    const gqlCart = data.cartDiscountCodesUpdate.cart;
    this._bus.emit('cart:server:full', { cart: gqlCart, source: 'discount' });
    // A removed coupon must take its free gift with it — drop any gift now left
    // without its code on the cart.
    this._reconcileOrphanGift();
    return { ok: true, cart: this._cache.getCart() };
  }

  async _clear() {
    this._cache.clear();
    return { ok: true };
  }

  async _sync() {
    const cached = this._cache.getCart();
    if (!cached.shopifyCartId) return { ok: false, reason: 'no cart' };
    const data = await this._gql(Q_CART, { cartId: cached.shopifyCartId });
    const gqlCart = data.cart;
    if (!gqlCart) {
      this._cache.clear();
      return { ok: false, reason: 'cart expired' };
    }
    this._bus.emit('cart:synced', { cart: gqlCart });
    // Self-heal a cart left holding only SI/gift over no real product (e.g. a
    // prior session that hit the optimistic-strip race).
    this._reconcileOrphanGift();
    this._reconcileOrphanSI();
    return { ok: true, cart: this._cache.getCart() };
  }

  /* ── GQL FETCH ── */

  async _gql(query, variables) {
    const res = await fetch(this._endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new Error('GQL HTTP ' + res.status);
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data;
  }
}

window.NTS = window.NTS || {};
